<script setup lang="ts">
/**
 * RecentCarriersMenu — Task G5 (map D8).
 *
 * Ops top-bar dropdown of the last 5 carrier profiles viewed, each expanding
 * to the 9 tab deep-links (?tab= values). Mirrors CAB's "Recent CAB Reports"
 * menu (Carrier Central nav).
 *
 * Lives in @claimshield/shared (alongside OpsLayout.vue, which renders it)
 * rather than @claimshield/ops — this package cannot depend on @claimshield/ops
 * (one-way dependency: ops -> shared only), so this reads the
 * 'ops-recent-carriers' localStorage key directly rather than importing
 * ops's carrierProfileStore. The WRITE side lives in
 * packages/ops/src/stores/carrierProfileStore.ts's loadHeader() — both sides
 * agree on the key name + shape ({dot, legal_name, ts}[], max 5, newest
 * first) as a lightweight contract rather than a cross-package import.
 * The 'carrier-profile' route name below is registered by the ops app's
 * router only — this component is ops-only in practice (like the rest of
 * OpsLayout.vue's hardcoded ops routes), just shared-package-located.
 */
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'

interface RecentCarrier {
  dot: string
  legal_name: string | null
  ts: number
}

const RECENT_CARRIERS_KEY = 'ops-recent-carriers'

function readRecentCarriers(): RecentCarrier[] {
  try {
    const raw = localStorage.getItem(RECENT_CARRIERS_KEY)
    return raw ? (JSON.parse(raw) as RecentCarrier[]) : []
  } catch {
    return []
  }
}

const router = useRouter()
const popoverRef = ref()
const expandedDot = ref<string | null>(null)

// Re-read on every popover open — recomputed via a bump counter since
// localStorage isn't reactive on its own.
const refreshTick = ref(0)
const recents = computed<RecentCarrier[]>(() => {
  void refreshTick.value
  return readRecentCarriers()
})

const TAB_ITEMS: { label: string; key: string }[] = [
  { label: 'General', key: 'general' },
  { label: 'OOS / SAFER', key: 'oos' },
  { label: 'Radius', key: 'radius' },
  { label: 'Inspections / Accidents', key: 'details' },
  { label: 'CSA / BASICs', key: 'csa' },
  { label: 'History', key: 'history' },
  { label: 'Analysis Central', key: 'analysis' },
  { label: 'Driver', key: 'driver' },
  { label: 'Fleet', key: 'fleet' },
]

function toggle(event: Event) {
  refreshTick.value++
  popoverRef.value?.toggle(event)
}

function toggleExpand(dot: string) {
  expandedDot.value = expandedDot.value === dot ? null : dot
}

function goTo(dot: string, tab: string) {
  popoverRef.value?.hide()
  void router.push({ name: 'carrier-profile', params: { dot }, query: { tab } })
}
</script>

<template>
  <div v-if="recents.length > 0" class="inline-block">
    <button
      class="rounded-lg p-2 text-secondary-600 hover:bg-secondary-100"
      title="Recent carriers"
      @click="toggle"
    >
      <i class="pi pi-history text-lg" />
    </button>
    <PPopover ref="popoverRef">
      <div class="w-72 max-h-96 overflow-y-auto">
        <div class="px-3 py-2 border-b border-secondary-200 sticky top-0 bg-white">
          <div class="text-xs font-semibold text-secondary-500 uppercase">Recent Carriers</div>
        </div>
        <div v-for="c in recents" :key="c.dot" class="border-b border-secondary-100 last:border-0">
          <button
            class="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-secondary-50"
            @click="toggleExpand(c.dot)"
          >
            <div class="min-w-0">
              <div class="text-sm font-medium text-secondary-800 truncate">
                {{ c.legal_name || c.dot }}
              </div>
              <div class="text-xs text-secondary-400 font-mono">{{ c.dot }}</div>
            </div>
            <i
              :class="expandedDot === c.dot ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
              class="text-xs text-secondary-400 flex-shrink-0"
            />
          </button>
          <div v-if="expandedDot === c.dot" class="pl-4 pb-1 bg-secondary-50">
            <button
              v-for="t in TAB_ITEMS"
              :key="t.key"
              class="w-full text-left px-3 py-1.5 text-xs text-secondary-600 hover:text-primary-600 hover:bg-white rounded"
              @click="goTo(c.dot, t.key)"
            >{{ t.label }}</button>
          </div>
        </div>
      </div>
    </PPopover>
  </div>
</template>
